import { Box, IconButton, Stack, Switch } from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from "@mui/x-data-grid";
import { ConfirmationDialog, CustomDataGrid } from "@shared/components";
import type { FormikHelpers } from "formik";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useSortModel } from "@shared/components/grid-components/hooks/useSortModel";
import { TableActions } from "@shared/utils/constants";
import {
  formatDateTime,
  getActionPermission,
  getCapitalizeString,
} from "@shared/utils/functions";
import { useDeleteTitleConfiguration } from "../services/useDeleteTitleConfiguration";
import { useGetAllTitleConfigurationsList } from "../services/useGetTitleConfiguration";
import { useUpdateTitleConfiguration } from "../services/useUpdateTitleConfiguration";
import {
  AllowedSortFields,
  mapApiMessageToFormikForm,
} from "../utils/constants";
import {
  ITitleConfigurationRow,
  SortField,
  TitleDataGridProps,
  TitleFormValues,
} from "../utils/types";
import TitleForm from "./TitleForm";
import { FormActions } from "@shared/utils/constant";
import { Can, useCan } from "@core/casl-provider";

const TitleDataGrid = ({
  paginationModel,
  setPaginationModel,
  visibleColumns,
}: TitleDataGridProps) => {
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("searchQuery") ?? "";

  const { mutate: deleteMutation } = useDeleteTitleConfiguration();
  const {
    mutate: updateFormMutation,
    mutateAsync: updateFormMutationAsync,
    isPending: isFormPending,
  } = useUpdateTitleConfiguration();
  const ability = useCan();

  const { sortModel, handleSortModelChange, orderBy, order } =
    useSortModel<SortField>({
      allowedSortFields: AllowedSortFields,
    });

  const { data: titleConfigurationsList, isLoading } =
    useGetAllTitleConfigurationsList({
      data: {
        pageNumber: String(paginationModel.page + 1),
        pageSize: String(paginationModel.pageSize),
        includeInactive: true,
        // Map unified searchQuery to titleName for backend filtering compatibility
        searchQuery: searchQuery,
        orderBy: orderBy,
        order: order,
      },
    });

  const [allConfigurations, setAllConfigurations] = useState<
    ITitleConfigurationRow[]
  >([]);

  const totalRows: number =
    (titleConfigurationsList?.pagination?.total as number) ??
    allConfigurations.length;

  const [actionState, setActionState] = useState<{
    action: "EDIT" | "DELETE" | "";
    payload: Partial<TitleFormValues> & { id?: number; titleName?: string };
  }>({
    action: "",
    payload: {},
  });

  // Confirm toggle state for boolean fields before applying
  const [pendingToggle, setPendingToggle] = useState<{
    id: ITitleConfigurationRow["id"];
    field: "pushEnabled" | "isActive";
    newValue: boolean;
    titleName?: string;
  } | null>(null);

  const clearActionState = () => {
    setActionState({ action: "", payload: {} });
  };

  const handleDeleteConfirm = async () => {
    if (actionState.payload.id) {
      await deleteMutation({ id: String(actionState.payload.id) });
    }
    clearActionState();
  };

  const handleEditSubmit = async (
    values: TitleFormValues,
    helpers: FormikHelpers<TitleFormValues & { id?: number }>
  ) => {
    const id = actionState.payload.id;
    const payloadData = { ...values };
    delete payloadData.id;

    try {
      await updateFormMutationAsync({
        payload: { id: id as number, payloadData },
      });
      clearActionState();
    } catch (error: unknown) {
      const errMsg: string =
        error instanceof Error
          ? error.message
          : "Failed to update configuration";
      const { errors, touched } = mapApiMessageToFormikForm(errMsg);
      helpers.setErrors(errors);
      helpers.setTouched(touched, false);
    } finally {
      helpers.setSubmitting(false);
    }
  };

  const handleOnChange = (event, params) => {
    if (!ability.can("update", "TitleConfiguration")) {
      enqueueSnackbar(
        "You don't have permission to update title configurations",
        { variant: "error" }
      );
      return;
    } else {
      setPendingToggle({
        id: params.row.id,
        field: "pushEnabled",
        newValue: event.target.checked,
        titleName: params.row.titleName,
      });
    }
  };

  const columns: GridColDef<ITitleConfigurationRow>[] = [
    {
      field: "titleName",
      headerName: "Name",
      flex: 1,
      minWidth: 160,
      sortable: true,
      renderCell: (
        params: GridRenderCellParams<ITitleConfigurationRow, string>
      ) => <span style={{ textTransform: "capitalize" }}>{params.value}</span>,
    },
    {
      field: "titleCode",
      headerName: "Code",
      flex: 1,
      minWidth: 140,
      sortable: true,
    },
    {
      field: "kafkaTopic",
      headerName: "Kafka Topic",
      flex: 1,
      minWidth: 160,
      sortable: false,
    },
    {
      field: "pushEnabled",
      headerName: "Push Enabled",
      flex: 0.6,
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (
        params: GridRenderCellParams<ITitleConfigurationRow, boolean>
      ) => (
        <Stack
          direction={"row"}
          spacing={1}
          alignItems={"center"}
          justifyContent={"start"}
          height={"100%"}
        >
          <Switch
            checked={Boolean(params.value)}
            size="small"
            onClick={(e) => e.stopPropagation()}
            onChange={(event) => handleOnChange(event, params)}
          />
        </Stack>
      ),
    },
    {
      field: "isActive",
      headerName: "Is Active",
      flex: 0.6,
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (
        params: GridRenderCellParams<ITitleConfigurationRow, boolean>
      ) => (
        <Stack
          direction={"row"}
          spacing={1}
          alignItems={"center"}
          justifyContent={"start"}
          height={"100%"}
        >
          <Switch
            // disabled={!ability.can("update", "TitleConfiguration")}
            checked={Boolean(params.value)}
            size="small"
            onClick={(e) => e.stopPropagation()}
            onChange={(event) => {
              if (!ability.can("update", "TitleConfiguration")) {
                enqueueSnackbar(
                  "You don't have permission to update title configurations",
                  { variant: "error" }
                );
                return;
              }
              setPendingToggle({
                id: params.row.id,
                field: "isActive",
                newValue: event.target.checked,
                titleName: params.row.titleName,
              });
            }}
          />
        </Stack>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 1,
      minWidth: 210,
      sortable: true,
      valueFormatter: (value) => formatDateTime(value),
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 1,
      minWidth: 210,
      sortable: true,
      valueFormatter: (value) => formatDateTime(value),
    },
    {
      field: "createdBy",
      headerName: "Created By",
      flex: 1,
      minWidth: 140,
      sortable: false,
    },
    {
      field: "updatedBy",
      headerName: "Updated By",
      flex: 1,
      minWidth: 140,
      sortable: false,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      headerAlign: "center",
      renderCell: (
        params: GridRenderCellParams<
          ITitleConfigurationRow,
          unknown,
          unknown,
          GridTreeNodeWithRender
        >
      ) => (
        <Stack
          direction="row"
          spacing={1}
          alignItems={"center"}
          sx={{ height: "100%" }}
        >
          {TableActions.map((action, index) => {
            const actionPermission = getActionPermission(action);

            return (
              <Can
                do={actionPermission}
                on="TitleConfiguration"
                key={index}
              >
                <IconButton
                  key={index}
                  title={action.label}
                  style={{
                    backgroundColor: "primary.main",
                  }}
                  onClick={() =>
                    setActionState({
                      action: action.key,
                      payload: {
                        id: params.row.id,
                        titleName: params.row.titleName,
                        titleCode: params.row.titleCode,
                        kafkaTopic: params.row.kafkaTopic,
                        pushEnabled: params.row.pushEnabled,
                        isActive: params.row.isActive,
                      },
                    })
                  }
                >
                  {action.icon}
                </IconButton>
              </Can>
            );
          })}
        </Stack>
      ),
    },
  ];

  const handleSwitchChange = useCallback(
    (
      id: ITitleConfigurationRow["id"],
      field: "pushEnabled" | "isActive",
      newValue: boolean
    ) => {
      // 1. Optimistic Update (Update the UI immediately)
      const originalConfigurations = allConfigurations;
      setAllConfigurations((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [field]: newValue } : row))
      );

      // 2. Prepare API payload
      const payloadData: Partial<ITitleConfigurationRow> = {
        [field]: newValue,
      };

      // 3. Call Update API
      updateFormMutation(
        { payload: { id: id, payloadData } },
        {
          onError: (error: Error) => {
            // Revert the state on API failure (Pessimistic update after failure)
            setAllConfigurations(originalConfigurations);
            enqueueSnackbar(`Failed to update ${field}: ${error.message}`, {
              variant: "error",
              autoHideDuration: 3000,
              preventDuplicate: true,
            });
          },
        }
      );
    },
    [allConfigurations, updateFormMutation]
  );

  useEffect(() => {
    if (titleConfigurationsList?.data) {
      setAllConfigurations(titleConfigurationsList.data);
    }
  }, [titleConfigurationsList]);

  // Build the confirmation dialog message for toggle actions
  const getConfirmationMessage = (toggle: typeof pendingToggle): string => {
    if (!toggle) return "Confirm";
    const name = getCapitalizeString(toggle.titleName);
    if (toggle.field === "pushEnabled") {
      return toggle.newValue
        ? `Are you sure you want to enable push for "${name}"?`
        : `Are you sure you want to disable push for "${name}"?`;
    }
    if (toggle.field === "isActive") {
      return toggle.newValue
        ? `Are you sure you want to mark "${name}" as Active?`
        : `Are you sure you want to mark "${name}" as Inactive?`;
    }
    return "Confirm";
  };

  const getConfirmationTitle = (toggle: typeof pendingToggle): string => {
    if (!toggle) return "Confirm";
    switch (toggle.field) {
      case "pushEnabled":
        return "Confirm Push Change";
      case "isActive":
        return "Confirm Status Change";
      default:
        return "Confirm";
    }
  };

  const handlePendingToggleConfirm = () => {
    if (pendingToggle) {
      handleSwitchChange(
        pendingToggle.id,
        pendingToggle.field,
        pendingToggle.newValue
      );
    }
    setPendingToggle(null);
  };

  return (
    <>
      <Box
        sx={(theme) => ({
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          borderBottomLeftRadius: theme.shape.borderRadius,
          borderBottomRightRadius: theme.shape.borderRadius,
          overflow: "hidden",
        })}
      >
        <CustomDataGrid<ITitleConfigurationRow>
          rows={allConfigurations}
          columns={columns}
          visibleColumns={visibleColumns}
          getRowId={(row) => row.id}
          rowCount={totalRows}
          paginationMode="server"
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          autoHeight
          disableSelectionOnClick
          loading={isLoading}
          dataGridSx={(theme) => ({
            "& .MuiDataGrid-columnHeaders": {
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            "& .MuiDataGrid-cell": {
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
          })}
        />
      </Box>

      <ConfirmationDialog
        open={!!pendingToggle}
        onClose={() => setPendingToggle(null)}
        onConfirm={handlePendingToggleConfirm}
        title={getConfirmationTitle(pendingToggle)}
        message={getConfirmationMessage(pendingToggle)}
        confirmText="Confirm"
        cancelText="Cancel"
        confirmColor="primary"
      />

      <ConfirmationDialog
        open={actionState.action === FormActions.DELETE}
        onClose={clearActionState}
        onConfirm={handleDeleteConfirm}
        title={`Delete ${actionState.payload.titleName}`}
        message="Are you sure you want to Delete?"
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="primary"
      />
      <TitleForm
        open={actionState.action === FormActions.EDIT}
        onClose={clearActionState}
        title={"Edit Title Configuration"}
        initialValues={actionState.payload}
        disable={isFormPending}
        onSubmit={handleEditSubmit}
        showCloseButton
      />
    </>
  );
};
export default TitleDataGrid;
